import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Divider,
  IconButton,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MembersIcon from "../../assets/common/space-members-icon.svg";
import ClientsIcon from "../../assets/common/space-clients-icon.svg";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";
import { SpaceMultiSelect } from "./CreateSpace";
import "../../styles/common.css";

interface User {
  id: string;
  name: string;
  email: string;
  pictureUrl?: string;
  profileOrgRelationshipId?: string;
}

interface AddMembersProps {
  open: boolean;
  onClose: () => void;
  spaceName: string;
  activeUsers: User[];
  existingMembers: User[];
  activeClients?: any[];
  existingClients?: any[];
  targetType?: "members" | "clients";
  onSave: (payload: {
    removeMemberProfileOrgIds: string[];
    spaceMemberProfileOrgIds: string[];
    subOrgIds?: string[];
    removeSubOrgIds?: string[];
  }) => void;
}

const AddMembers = ({
  open,
  onClose,
  spaceName,
  activeUsers = [],
  existingMembers = [],
  activeClients = [],
  existingClients = [],
  targetType,
  onSave,
}: AddMembersProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [clientSearchValue, setClientSearchValue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [clientExpanded, setClientExpanded] = useState(false);

  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [tempSelectedMembers, setTempSelectedMembers] = useState<string[]>([]);
  const [initialMemberIds, setInitialMemberIds] = useState<string[]>([]);

  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [tempSelectedClients, setTempSelectedClients] = useState<string[]>([]);
  const [initialClientIds, setInitialClientIds] = useState<string[]>([]);

  const getClientId = (c: any) => c.subOrgId || c.clientOrgId || c.id || "";
  const getClientName = (c: any) => c.subOrgName || c.clientName || c.name || "";

  useEffect(() => {
    if (open) {
      const mIds = existingMembers.map((m) => m.profileOrgRelationshipId || m.id).filter(Boolean) as string[];
      setSelectedMembers(mIds);
      setTempSelectedMembers(mIds);
      setInitialMemberIds(mIds);
      const cIds = existingClients.map((c) => getClientId(c)).filter(Boolean) as string[];
      setSelectedClients(cIds);
      setTempSelectedClients(cIds);
      setInitialClientIds(cIds);
    }
  }, [open]);

  const handleCloseAndReset = () => {
    setSearchValue("");
    setClientSearchValue("");
    setExpanded(false);
    setClientExpanded(false);
    onClose();
  };

  const handleOpenMembersDropdown = () => {
    setTempSelectedMembers(selectedMembers);
    setExpanded(true);
  };

  const handleToggleTempMember = (profileOrgRelationshipId: string) => {
    setTempSelectedMembers((prev) =>
      prev.includes(profileOrgRelationshipId)
        ? prev.filter((m) => m !== profileOrgRelationshipId)
        : [...prev, profileOrgRelationshipId]
    );
  };

  const handleApplyMembers = () => {
    setSelectedMembers(tempSelectedMembers);
    setExpanded(false);
  };

  const handleRemoveMemberChip = (profileOrgRelationshipId: string) => {
    setSelectedMembers((prev) =>
      prev.filter((m) => m !== profileOrgRelationshipId)
    );
    setTempSelectedMembers((prev) =>
      prev.filter((m) => m !== profileOrgRelationshipId)
    );
  };

  const handleOpenClientsDropdown = () => {
    setTempSelectedClients(selectedClients);
    setClientExpanded(true);
  };

  const handleToggleTempClient = (clientId: string) => {
    setTempSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((c) => c !== clientId)
        : [...prev, clientId]
    );
  };

  const handleApplyClients = () => {
    setSelectedClients(tempSelectedClients);
    setClientExpanded(false);
  };

  const handleRemoveClientChip = (clientId: string) => {
    setSelectedClients((prev) => prev.filter((c) => c !== clientId));
    setTempSelectedClients((prev) => prev.filter((c) => c !== clientId));
  };

  const areArraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    const set2 = new Set(arr2);
    return arr1.every((item) => set2.has(item));
  };

  const hasSelectionChanged = useMemo(() => {
    const membersChanged = !areArraysEqual(selectedMembers, initialMemberIds);
    const clientsChanged = !areArraysEqual(selectedClients, initialClientIds);

    if (targetType === "members") return membersChanged;
    if (targetType === "clients") return clientsChanged;
    return membersChanged || clientsChanged;
  }, [selectedMembers, initialMemberIds, selectedClients, initialClientIds, targetType]);

  const handleSave = () => {
    const removeMemberProfileOrgIds = initialMemberIds.filter(
      (id) => !selectedMembers.includes(id)
    );
    const spaceMemberProfileOrgIds = selectedMembers;

    const removeSubOrgIds = initialClientIds.filter(
      (id) => !selectedClients.includes(id)
    );
    const subOrgIds = selectedClients;

    onSave({
      removeMemberProfileOrgIds,
      spaceMemberProfileOrgIds,
      subOrgIds,
      removeSubOrgIds,
    });
    handleCloseAndReset();
  };

  const filteredUsers = useMemo(() => {
    if (!searchValue) return activeUsers;
    return activeUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, activeUsers]);

  const allUserOptions = useMemo(() => {
    const map = new Map<string, User>();
    existingMembers.forEach((u) => {
      const id = u.profileOrgRelationshipId;
      if (id) map.set(id, u);
    });
    activeUsers.forEach((u) => {
      const id = u.profileOrgRelationshipId;
      if (id) map.set(id, u);
    });
    return Array.from(map.values());
  }, [existingMembers, activeUsers]);

  const selectedUsers = useMemo(() => {
    return allUserOptions.filter((u) =>
      selectedMembers.includes(u.profileOrgRelationshipId || u.id)
    );
  }, [allUserOptions, selectedMembers]);

  const filteredClients = useMemo(() => {
    if (!clientSearchValue) return activeClients;
    return activeClients.filter((client) => {
      const clientName = getClientName(client);
      return (
        clientName.toLowerCase().includes(clientSearchValue.toLowerCase())
      );
    });
  }, [clientSearchValue, activeClients]);

  const allClientOptions = useMemo(() => {
    const map = new Map<string, any>();
    existingClients.forEach((c) => {
      const id = getClientId(c);
      if (id) map.set(id, c);
    });
    activeClients.forEach((c) => {
      const id = getClientId(c);
      if (id) map.set(id, c);
    });
    return Array.from(map.values());
  }, [existingClients, activeClients]);

  const selectedClientsList = useMemo(() => {
    return allClientOptions.filter((c) => {
      const id = getClientId(c);
      return selectedClients.includes(id);
    });
  }, [allClientOptions, selectedClients]);

  const showMembersSection = !targetType || targetType === "members";
  const showClientsSection = !targetType || targetType === "clients";

  return (
    <Dialog
      open={open}
      onClose={handleCloseAndReset}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        className: "create-space-paper",
        sx: { overflow: "hidden", borderRadius: "8px" },
      }}
    >
      <DialogTitle className="create-space-header">
        <IconButton
          onClick={handleCloseAndReset}
          className="create-space-close-btn"
        >
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>

        <Typography className="create-space-title">
          {targetType === "clients"
            ? "Add Clients"
            : targetType === "members"
            ? "Add Members"
            : spaceName || "Add Members"}
        </Typography>
        <Typography className="create-space-subtitle">
          {spaceName ? `Space: ${spaceName}` : "A space represents teams, departments, or groups."}
        </Typography>
      </DialogTitle>

      <DialogContent
        className="create-space-content"
        sx={{ maxHeight: "60vh", overflowY: "auto", scrollbarWidth: "thin" }}
      >
        {/* ADD MEMBERS */}
        {showMembersSection && (
          <SpaceMultiSelect
            iconSrc={MembersIcon}
            sectionLabel="ADD MEMBERS"
            required
            searchPlaceholder="Search by name or enter email..."
            searchValue={searchValue}
            onSearchChange={(val) => {
              setSearchValue(val);
              if (!expanded) handleOpenMembersDropdown();
            }}
            expanded={expanded}
            onOpenDropdown={handleOpenMembersDropdown}
            onCloseDropdown={() => setExpanded(false)}
            selectedChips={selectedUsers.map((u) => ({
              id: u.profileOrgRelationshipId || u.id,
              label: u.name,
            }))}
            onRemoveChip={handleRemoveMemberChip}
            options={filteredUsers}
            tempSelectedIds={tempSelectedMembers}
            onToggleTempItem={handleToggleTempMember}
            onApply={handleApplyMembers}
            getOptionId={(u) => u.profileOrgRelationshipId || u.id}
            getOptionName={(u) => u.name}
            renderAvatar={(u) => (
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <Avatar
                  src={u.pictureUrl || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "12px",
                    fontWeight: 600,
                    bgcolor: !u.pictureUrl
                      ? getAvatarColor(u.email || u.name)
                      : undefined,
                  }}
                >
                  {!u.pictureUrl && getAvatarInitials(u.email || u.name)}
                </Avatar>
              </ListItemAvatar>
            )}
            emptyText="No members found"
          />
        )}

        {/* ADD CLIENTS */}
        {showClientsSection && (
          <SpaceMultiSelect
            iconSrc={ClientsIcon}
            sectionLabel="ADD CLIENTS"
            searchPlaceholder="Search client by name.."
            searchValue={clientSearchValue}
            onSearchChange={(val) => {
              setClientSearchValue(val);
              if (!clientExpanded) handleOpenClientsDropdown();
            }}
            expanded={clientExpanded}
            onOpenDropdown={handleOpenClientsDropdown}
            onCloseDropdown={() => setClientExpanded(false)}
            selectedChips={selectedClientsList.map((c) => ({
              id: getClientId(c),
              label: getClientName(c),
            }))}
            onRemoveChip={handleRemoveClientChip}
            options={filteredClients}
            tempSelectedIds={tempSelectedClients}
            onToggleTempItem={handleToggleTempClient}
            onApply={handleApplyClients}
            getOptionId={getClientId}
            getOptionName={getClientName}
            emptyText="No clients found"
            containerSx={{ pb: 2 }}
          />
        )}
      </DialogContent>

      <Divider sx={{ border: "1px solid #F0F0F4" }} />

      <DialogActions className="create-space-actions">
        <Button
          onClick={handleCloseAndReset}
          className="create-space-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          className="create-space-submit-btn"
          disabled={!hasSelectionChanged}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMembers;